import { Injectable } from '@angular/core';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { Schedule } from '../schedule/schedule';
import { Booking } from '../booking/booking';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { PastPrivateJobPage } from 'src/pages/more/past-private-job/past-private-job';
import { PastJobPage } from 'src/pages/more/past-job/past-job';
import { JobPage } from 'src/pages/schedule/job/job';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { ReportIssue } from '../report-issue/report-issue';
import { PaidInvoicePage } from 'src/pages/invoices/paid-invoice/paid-invoice';
import { PendingInvoicePage } from 'src/pages/invoices/pending-invoice/pending-invoice';
import { Util } from 'src/shared/util/util';
import { Invoices } from '../invoices/invoices';
import { Communication } from 'src/providers/communication/communication';
import { getSellerDisplay } from 'src/shared/util/get-seller-display';

@Injectable({
  providedIn: "root",
})
export class NotificationsRedirectService {

  constructor(
    private rightSidePanelService: RightSidePanelService,
    private navCtrl: CustomNavController,
    private storage: TidyStorage,
    private schedule: Schedule,
    private booking: Booking,
    private reportIssue: ReportIssue,
    private util: Util,
    private invoices: Invoices,
    private communication: Communication
  ) {}

  public static readonly PAGES = [
    'no_page', //handled in notification-center.ts
    'home_page', // done
    'customer_booking_page', // done
    'address_page', // done
    'homekeeper_page',
    'guest_reservation_source_page',
    'proposal_page',
    'invoice_page', // done
    'chat_page', // done
    'inquiry_page', // inquiry / booking request — opens the host Inquiries list
    // 'homekeeper_job_page', // pro app
    // 'customer_page', // pro app
    // 'opportunities_page',  // pro app
    // 'job_delegation_page', // pro app
    // 'payouts_page',         // pro app
  ];

  async redirectToPage(pageData: {name: string, data?: any, [key: string]: any}) {
    try {
      const pageName = pageData.name.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      if (typeof this[pageName] === 'function') {
        await this[pageName](pageData.data);
      }
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
      this.homePage({});
    }
  }

  async chatPage(data) {
    this.communication.markMessagesAsRead([data.chat_room_key]);
    const pageTitle = data.notification?.title.includes(':') ? data.notification?.title.split(':')[0] : data.notification?.title.split(' ').slice(-2)[0];
    const params = {
      backPage: 'notification-center',
      chatRoomKey: data.chat_room_key,
      cameFromNotificationsPage: true,
      pageTitle
    };
    this.rightSidePanelService.navigateTo('message-thread', params);
  }

  private homePage(_data: any): Promise<any> {
    return this.navCtrl.navigateRoot('properties');
  }

  // DEPRECATED
  private jobPage(data: any): Promise<any> {
    return this.customerBookingPage(data);
  }

  // eg.
  // data: { booking_id: 880987, job_progress: 'completed' }
  private customerBookingPage(data: any): Promise<any> {
    return this.goToCleaningDetail(data);
  }

  // eg.
  // data: { invoice_id: 123 }
  private invoicePage(data: any): Promise<any> {
    return this.goToInvoicePage(data);
  }

  private addressPage(data: any): Promise<any> {
    return this.navCtrl.navigateForward(`properties?propertyId=${data.address_id}`);
  }

  // Inquiries: a guest submitted a booking request on one of the host's channels
  // (direct booking, Airbnb, VRBO, …). Opens the host Inquiries list.
  // BACKEND CONTRACT: the gotidy notification must send
  // page_data.name = 'inquiry_page' (Inquiry/notification backend — card
  // d9rpG2zZ). Could later deep-link to a specific inquiry id.
  private inquiryPage(_data: any): Promise<any> {
    return this.navCtrl.navigateForward('inquiries');
  }

  // Redirect handling

  private isPastJob(jobProgress: string) {
    const parsedJobProgress = this.reportIssue.getJobStatusData([], jobProgress).name;
    return parsedJobProgress === 'completed' ||
           parsedJobProgress === 'cancelled';
  }

  private async goToCleaningDetail(data: any): Promise<any> {
    localStorage.setItem('jobBackPage', 'notification-center');
    const booking = await this.booking.getBookingDetail(data.booking_id);
    const jobId = booking.current_job.id;
    const addressId = booking.address_id;
    const isPastJob = this.isPastJob(data.job_progress);
    const isPrivateJob = booking.current_job.is_private;
    await this.navigateToJobPageInsideDialog(jobId, addressId, isPastJob, isPrivateJob);
  }

  private async navigateToJobPageInsideDialog(jobId: number, addressId: number, isPastJob: boolean, isPrivateJob: boolean): Promise<void> {
    if (isPastJob && isPrivateJob) {
      const params = { jobId, addressId, backPage: 'notification-center' };
      this.rightSidePanelService.navigateTo(`past-private-job/${jobId}`, params, PastPrivateJobPage);
    } else if (isPastJob) {
      const params = { jobId, addressId, backPage: 'notification-center' };
      this.rightSidePanelService.navigateTo(`past-job/${jobId}`, params, PastJobPage);
    } else {
      const cleaningCard = await this.schedule.getJobCard(addressId, jobId);
      const params = { jobId, addressId, card: cleaningCard, job: cleaningCard?.template_data?.job, jobBackPage: 'notification-center' };
      this.rightSidePanelService.navigateTo(`job/${addressId}/${jobId}`, params, JobPage);
    }
  }

  private async goToInvoicePage(data: any): Promise<any> {
    try {
      const invoice = await this.invoices.getInvoiceById(data.invoice_id);
      const seller = getSellerDisplay(invoice);
      const params: any = {
        invoiceId: invoice.id,
        hasAccount: true,
        proName: seller.name,
        proEmail: seller.email,
        proPhone: seller.phone,
        proTeamId: seller.teamId,
        proId: invoice?.seller?.data?.team_owner?.id ?? invoice?.homekeeper_data?.id,
        isLoggedIntoClientApp: true,
        successRoute: 'bills',
        invoice,
      }
      const path = invoice.full_paid ? `paid-bill/${invoice.id}` : `pending-bill/${invoice.id}`;
      const component = invoice.full_paid ? PaidInvoicePage : PendingInvoicePage;
      this.rightSidePanelService.navigateTo(path, params, component);
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

}
